import webSocketService from './WebSocketService';

interface UserOnlineStatus {
   userId: string;
   username: string;
   isOnline: boolean;
   lastSeen: Date;
}

class OnlineStatusService {
   private onlineUsers: Map<string, UserOnlineStatus> = new Map();
   private listeners: ((onlineUsers: Map<string, UserOnlineStatus>) => void)[] = [];

   // Auto-detection properties
   private currentUserId: string | null = null;
   private currentUsername: string | null = null;
   private isCurrentUserOnline: boolean = true;
   private heartbeatInterval: NodeJS.Timeout | null = null;
   private heartbeatTimeout: NodeJS.Timeout | null = null;
   private visibilityChangeTimeout: NodeJS.Timeout | null = null;
   private isInitialized: boolean = false;

   // Configuration
   private readonly HEARTBEAT_INTERVAL = 30000; // 30 seconds
   private readonly HEARTBEAT_TIMEOUT = 10000; // 10 seconds to wait for pong
   private readonly OFFLINE_DELAY = 5000; // 5 seconds delay before going offline
   private readonly ONLINE_DELAY = 1000; // 1 second delay before going online

   constructor() {
      this.setupWebSocketListeners();
      this.initializeAutoDetection();
   }

   private setupWebSocketListeners() {
      if (!webSocketService) return;

      // Listen for user status updates from other users
      webSocketService.on(
         'user-status-changed',
         (status: { userId: string; username: string; isOnline: boolean }) => {
            this.updateUserStatusFromEvent(status);
         }
      );

      // Listen for connection status changes
      webSocketService.on('connect', () => {
         if (this.currentUserId && this.currentUsername) {
            this.updateCurrentUserStatus(true, true); // Immediate update on connection
         }
      });

      webSocketService.on('disconnect', () => {
         // Don't automatically set offline on disconnect as it might be temporary
      });

      // Listen for reconnection
      webSocketService.on('reconnected', () => {
         if (this.currentUserId && this.currentUsername) {
            this.updateCurrentUserStatus(true, true); // Immediate update on reconnection
         }
      });

      // Handle network status changes
      webSocketService.on(
         'network_status_changed',
         (status: { isOnline: boolean; supported: boolean }) => {
            if (status.isOnline) {
               this.handleNetworkOnline();
            } else {
               this.handleNetworkOffline();
            }
         }
      );

      // Listen for WebSocket errors
      webSocketService.on('error', (error: Error) => {
         console.error('WebSocket error affecting online status:', error);
         // Don't go offline immediately on WebSocket errors, wait for network detection
      });
   }

   // Initialize auto-detection mechanisms
   private initializeAutoDetection() {
      if (typeof window === 'undefined') return; // Skip in SSR

      // Handle page visibility changes
      document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

      // Handle window focus/blur
      window.addEventListener('focus', this.handleWindowFocus.bind(this));
      window.addEventListener('blur', this.handleWindowBlur.bind(this));

      // Handle network status
      window.addEventListener('online', this.handleNetworkOnline.bind(this));
      window.addEventListener('offline', this.handleNetworkOffline.bind(this));

      // Handle user activity
      ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach((event) => {
         document.addEventListener(event, this.handleUserActivity.bind(this), true);
      });

      // Handle page unload
      window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
      window.addEventListener('unload', this.handleUnload.bind(this));
   }

   // Initialize current user and start heartbeat
   public initializeCurrentUser(userId: string, username: string) {
      if (this.isInitialized && this.currentUserId === userId) {
         return;
      }

      this.currentUserId = userId;
      this.currentUsername = username;
      this.isCurrentUserOnline = true;

      // Enable auto-detection features
      if (!this.isInitialized) {
         this.enableAutoDetectionFeatures();
         this.isInitialized = true;
      }

      // Immediately publish current user status and start heartbeat
      this.updateCurrentUserStatus(true, true);
   }

   // Enable all auto-detection features
   private enableAutoDetectionFeatures() {
      this.initializeAutoDetection();
      this.setupWebSocketListeners();
      this.startHeartbeat();
   }

   // Start heartbeat mechanism
   private startHeartbeat() {
      this.stopHeartbeat(); // Clear any existing heartbeat

      this.heartbeatInterval = setInterval(() => {
         this.sendHeartbeat();
      }, this.HEARTBEAT_INTERVAL);
   }

   // Stop heartbeat mechanism
   private stopHeartbeat() {
      if (this.heartbeatInterval) {
         clearInterval(this.heartbeatInterval);
         this.heartbeatInterval = null;
      }
      if (this.heartbeatTimeout) {
         clearTimeout(this.heartbeatTimeout);
         this.heartbeatTimeout = null;
      }
   }

   // Send heartbeat ping
   private sendHeartbeat() {
      if (!webSocketService || !this.currentUserId) return;

      try {
         // Check connection status instead of sending custom ping
         if (webSocketService.getConnectionStatus()) {
            // Connection is healthy, ensure we're marked as online
            if (!this.isCurrentUserOnline) {
               this.updateCurrentUserStatus(true);
            }
         } else {
            // Connection is lost, mark as offline
            this.updateCurrentUserStatus(false);
         }
      } catch (error) {
         // Connection might be lost
         this.updateCurrentUserStatus(false);
      }
   }

   // Handle heartbeat response
   private handleHeartbeatResponse() {
      // Clear the timeout since connection is healthy
      if (this.heartbeatTimeout) {
         clearTimeout(this.heartbeatTimeout);
         this.heartbeatTimeout = null;
      }
      
      // Ensure we're marked as online if we weren't already
      if (!this.isCurrentUserOnline) {
         this.updateCurrentUserStatus(true);
      }
   }

   // Handle visibility change
   private handleVisibilityChange() {
      if (this.visibilityChangeTimeout) {
         clearTimeout(this.visibilityChangeTimeout);
      }

      if (document.hidden) {
         // Page became hidden, wait before going offline
         this.visibilityChangeTimeout = setTimeout(() => {
            this.updateCurrentUserStatus(false);
         }, this.OFFLINE_DELAY);
      } else {
         // Page became visible, immediately go online
         this.updateCurrentUserStatus(true, true);
      }
   }

   // Handle window focus
   private handleWindowFocus() {
      this.updateCurrentUserStatus(true, true);
   }

   // Handle window blur
   private handleWindowBlur() {
      // Don't immediately go offline on blur, user might just be switching tabs
      setTimeout(() => {
         if (document.hidden) {
            this.updateCurrentUserStatus(false);
         }
      }, this.OFFLINE_DELAY);
   }

   // Handle network online
   private handleNetworkOnline() {
      this.updateCurrentUserStatus(true, true);
   }

   // Handle network offline
   private handleNetworkOffline() {
      this.updateCurrentUserStatus(false, true);
   }

   // Handle user activity
   private handleUserActivity() {
      // User is active, ensure they're marked as online
      if (!this.isCurrentUserOnline) {
         this.updateCurrentUserStatus(true);
      }
   }

   // Handle before unload
   private handleBeforeUnload() {
      this.updateCurrentUserStatus(false, true);
   }

   // Handle unload
   private handleUnload() {
      this.updateCurrentUserStatus(false, true);
   }

   // Update current user status with auto-publishing
   private updateCurrentUserStatus(isOnline: boolean, immediate: boolean = false) {
      if (!this.currentUserId || !this.currentUsername) return;

      // Don't update if the status hasn't changed
      if (this.isCurrentUserOnline === isOnline) return;

      this.isCurrentUserOnline = isOnline;

      const publishStatus = async () => {
         try {
            // Update local status first
            this.updateUserStatus(this.currentUserId!, this.currentUsername!, isOnline);

            // Publish to WebSocket if available
            if (webSocketService) {
               if (isOnline) {
                  webSocketService.setUserOnline(this.currentUserId!, this.currentUsername!);
               } else {
                  webSocketService.setUserOffline(this.currentUserId!, this.currentUsername!);
               }
            }
         } catch (error) {
            console.error('Failed to publish user status:', error);
         }
      };

      if (immediate) {
         publishStatus();
      } else {
         // Debounce status updates to avoid spamming
         const delay = isOnline ? this.ONLINE_DELAY : this.OFFLINE_DELAY;
         setTimeout(publishStatus, delay);
      }
   }

   // Update user online status - Enhanced to handle multiple users better
   updateUserStatus(userId: string, username: string, isOnline: boolean) {
      // Don't allow external updates to overwrite current user status
      if (this.isCurrentUser(userId)) {
         return;
      }

      const currentStatus = this.onlineUsers.get(userId);
      const now = new Date();

      // Only update if status actually changed or if we don't have existing status
      if (!currentStatus || currentStatus.isOnline !== isOnline) {
         const newStatus: UserOnlineStatus = {
            userId,
            username,
            isOnline,
            lastSeen: now,
         };

         this.onlineUsers.set(userId, newStatus);
         this.notifyListeners();
      }
   }

   // Update user online status from WebSocket event
   updateUserStatusFromEvent(status: { userId: string; username: string; isOnline: boolean }) {
      this.updateUserStatus(status.userId, status.username, status.isOnline);
   }

   // Check if user is online
   isUserOnline(userId: string): boolean {
      const user = this.onlineUsers.get(userId);
      return user?.isOnline || false;
   }

   // Get user status (compatible with old interface)
   async getUserOnlineStatus(userId: string): Promise<UserOnlineStatus | null> {
      return this.getUserStatus(userId);
   }

   // Get multiple users' online status (compatible with old interface)
   async getMultipleUsersOnlineStatus(userIds: string[]): Promise<UserOnlineStatus[]> {
      return userIds
         .map((userId) => this.getUserStatus(userId))
         .filter(Boolean) as UserOnlineStatus[];
   }

   // Get user status
   getUserStatus(userId: string): UserOnlineStatus | null {
      return this.onlineUsers.get(userId) || null;
   }

   // Get all online users
   getOnlineUsers(): UserOnlineStatus[] {
      return Array.from(this.onlineUsers.values()).filter((user) => user.isOnline);
   }

   // Get all users (online and recently offline)
   getAllUsers(): UserOnlineStatus[] {
      return Array.from(this.onlineUsers.values());
   }

   // Subscribe to status changes
   subscribe(listener: (onlineUsers: Map<string, UserOnlineStatus>) => void) {
      this.listeners.push(listener);
      return () => {
         this.listeners = this.listeners.filter((l) => l !== listener);
      };
   }

   // Notify all listeners
   private notifyListeners() {
      this.listeners.forEach((listener) => {
         try {
            listener(this.onlineUsers);
         } catch (error) {
            console.error('Error in online status listener:', error);
         }
      });
   }

   // Set current user online (called when app starts) - Enhanced
   setCurrentUserOnline(userId: string, username: string) {
      this.initializeCurrentUser(userId, username);
   }

   // Set current user offline (called when app closes) - Enhanced
   setCurrentUserOffline(userId: string, username: string) {
      this.updateCurrentUserStatus(false, true);
      this.stopHeartbeat();
   }

   // Manual refresh of online users from server
   async refreshOnlineUsers(): Promise<void> {
      try {
         // This would typically fetch from your API
         // The actual refresh will happen through WebSocket events
      } catch (error) {
         console.error('❌ Failed to refresh online users:', error);
      }
   }

   // Get current user online status
   getCurrentUserStatus(): boolean {
      return this.isCurrentUserOnline;
   }

   // Get multiple user statuses efficiently
   getMultipleUserStatuses(userIds: string[]): { [userId: string]: UserOnlineStatus | null } {
      const statuses: { [userId: string]: UserOnlineStatus | null } = {};

      userIds.forEach((userId) => {
         statuses[userId] = this.onlineUsers.get(userId) || null;
      });

      return statuses;
   }

   // Check if a user is the current user
   isCurrentUser(userId: string): boolean {
      return userId === this.currentUserId;
   }

   // Clean up all resources
   destroy() {
      this.stopHeartbeat();

      if (typeof window !== 'undefined') {
         document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
         window.removeEventListener('focus', this.handleWindowFocus.bind(this));
         window.removeEventListener('blur', this.handleWindowBlur.bind(this));
         window.removeEventListener('online', this.handleNetworkOnline.bind(this));
         window.removeEventListener('offline', this.handleNetworkOffline.bind(this));
         window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
         window.removeEventListener('unload', this.handleUnload.bind(this));
         document.removeEventListener('mousemove', this.handleUserActivity.bind(this));
         document.removeEventListener('keypress', this.handleUserActivity.bind(this));
         document.removeEventListener('click', this.handleUserActivity.bind(this));
         document.removeEventListener('scroll', this.handleUserActivity.bind(this));
      }

      if (this.visibilityChangeTimeout) {
         clearTimeout(this.visibilityChangeTimeout);
      }

      this.listeners = [];
      this.onlineUsers.clear();

   }
}

// Create and export singleton
const onlineStatusService = new OnlineStatusService();
export default onlineStatusService;
